import { useEffect, useRef, useState } from "react";
import { motion, useReducedMotion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowRight, CalendarDays, Check } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { fmtDate } from "@/components/shared";
import { ProjectIcon, PROJECT_STATUS } from "@/components/ShareVisuals";

const ease = [0.22, 1, 0.36, 1];

export function useLightMode() {
  const reduce = useReducedMotion();
  const [light, setLight] = useState(true);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px), (pointer: coarse)");
    const upd = () => setLight(mq.matches);
    upd(); mq.addEventListener("change", upd);
    return () => mq.removeEventListener("change", upd);
  }, []);
  return { light, reduce: !!reduce };
}

export function ComingSoonBadge({ label = "COMING SOON", testId = "coming-soon-badge", size = "sm" }) {
  return (
    <span data-testid={testId} className={`cs-badge relative inline-flex items-center gap-1.5 rounded-full border border-cyan-400/40 bg-cyan-500/10 text-cyan-200 font-mono font-semibold tracking-[0.18em] overflow-hidden ${size === "lg" ? "px-4 py-1.5 text-xs" : "px-2.5 py-1 text-[10px]"}`}>
      <span className="cs-badge-dot w-1.5 h-1.5 rounded-full bg-cyan-300" aria-hidden="true" />
      {label}
      <span className="cs-badge-shimmer" aria-hidden="true" />
    </span>
  );
}

export function StatusDot({ status, testId }) {
  const [label, cls] = PROJECT_STATUS[status] || PROJECT_STATUS.coming_soon;
  return (
    <span data-testid={testId || `project-status-${status}`} className={`inline-flex items-center gap-2 text-xs ${cls.split(" ")[1]}`}>
      <span className="relative inline-flex w-2 h-2"><span className="status-ping absolute inset-0 rounded-full bg-current opacity-60" /><span className="relative w-2 h-2 rounded-full bg-current" /></span>
      {label}
    </span>
  );
}

function CardParticles({ count = 6 }) {
  return (
    <span className="pointer-events-none absolute inset-0 overflow-hidden rounded-2xl" aria-hidden="true">
      {Array.from({ length: count }, (_, i) => (
        <span key={i} className="card-particle" style={{ left: `${10 + ((i * 53) % 80)}%`, animationDelay: `${-(i * 1.7)}s`, animationDuration: `${7 + (i % 4) * 2}s`, width: 2 + (i % 2), height: 2 + (i % 2) }} />
      ))}
    </span>
  );
}

function TiltCard({ children, className = "", disabled, ...rest }) {
  const mx = useMotionValue(0.5), my = useMotionValue(0.5);
  const rx = useSpring(useTransform(my, [0, 1], [7, -7]), { stiffness: 180, damping: 22 });
  const ry = useSpring(useTransform(mx, [0, 1], [-7, 7]), { stiffness: 180, damping: 22 });
  const glowX = useTransform(mx, (v) => `${v * 100}%`), glowY = useTransform(my, (v) => `${v * 100}%`);
  const glowBg = useTransform([glowX, glowY], ([x, y]) => `radial-gradient(240px circle at ${x} ${y}, rgba(0,240,255,.14), transparent 60%)`);
  const onMove = (e) => { if (disabled) return; const r = e.currentTarget.getBoundingClientRect(); mx.set((e.clientX - r.left) / r.width); my.set((e.clientY - r.top) / r.height); };
  const onLeave = () => { mx.set(0.5); my.set(0.5); };
  return (
    <motion.div onMouseMove={onMove} onMouseLeave={onLeave} style={disabled ? {} : { rotateX: rx, rotateY: ry, transformPerspective: 900 }} className={className} {...rest}>
      {!disabled && <motion.span aria-hidden="true" className="pointer-events-none absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" style={{ background: glowBg }} />}
      {children}
    </motion.div>
  );
}

const seq = { hidden: {}, show: { transition: { staggerChildren: 0.07, delayChildren: 0.05 } } };
const item = { hidden: { opacity: 0, y: 10 }, show: { opacity: 1, y: 0, transition: { duration: 0.45, ease } } };

export function ShowcaseCard({ project, index = 0, onOpen, light, reduce }) {
  const selected = index % 3 === 0;
  return (
    <motion.div initial={reduce ? false : { opacity: 0, y: 34 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7, ease, delay: reduce ? 0 : Math.min((index % 3) * 0.1, 0.3) }} className="h-full">
      <TiltCard disabled={light || reduce} data-testid={`project-card-${project.id}`} onClick={() => onOpen(project)} role="button" tabIndex={0}
        onKeyDown={(e) => e.key === "Enter" && onOpen(project)}
        className="showcase-card group relative h-full rounded-2xl p-6 bg-[#0B1224]/85 backdrop-blur-xl border border-cyan-500/15 cursor-pointer transition-[border-color,box-shadow,transform] duration-500 hover:border-cyan-400/50 hover:shadow-[0_24px_70px_-20px_rgba(0,200,255,0.4)] hover:-translate-y-1.5 flex flex-col">
        <span className="showcase-border" aria-hidden="true" />
        <motion.span initial={reduce ? false : { opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.8 }} aria-hidden="true"
          className="pointer-events-none absolute -top-10 -left-10 w-40 h-40 rounded-full bg-cyan-500/15 blur-3xl showcase-glow" />
        {!light && !reduce && <span className={selected ? "" : "opacity-0 group-hover:opacity-100 transition-opacity duration-700"}><CardParticles count={selected ? 7 : 4} /></span>}
        <motion.div variants={seq} initial={reduce ? false : "hidden"} whileInView="show" viewport={{ once: true, margin: "-60px" }} className="relative flex flex-col flex-1">
          <div className="flex items-start justify-between gap-3">
            <motion.div variants={item} className={`w-14 h-14 rounded-2xl bg-gradient-to-br from-cyan-500/20 to-blue-600/10 border border-cyan-400/25 flex items-center justify-center text-cyan-300 overflow-hidden showcase-icon ${reduce ? "" : "icon-float"}`}>
              {project.image_url ? <img src={project.image_url} alt="" className="w-full h-full object-cover" /> : <ProjectIcon name={project.icon} className="w-7 h-7" />}
            </motion.div>
            <motion.div variants={item}><ComingSoonBadge label={(PROJECT_STATUS[project.status]?.[0] || "COMING SOON").toUpperCase()} testId={`coming-soon-${project.id}`} /></motion.div>
          </div>
          <motion.p variants={item} className="label-eyebrow text-[10px] font-mono uppercase tracking-[0.22em] text-cyan-400 mt-5">{project.category}</motion.p>
          <motion.h3 variants={item} className="font-heading text-lg font-semibold text-white mt-1">{project.name}</motion.h3>
          <motion.p variants={item} className="text-sm text-slate-400 mt-2 leading-relaxed flex-1">{project.description}</motion.p>
          <motion.div variants={item} className="mt-5 flex items-center justify-between gap-3">
            <StatusDot status={project.status} testId={`status-dot-${project.id}`} />
            <span className="inline-flex items-center gap-1.5 text-sm text-cyan-300 group-hover:text-cyan-200 transition-colors">Learn More <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" /></span>
          </motion.div>
        </motion.div>
      </TiltCard>
    </motion.div>
  );
}

function NetworkCanvas({ light, reduce }) {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current; if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const N = light ? 18 : 34, LINK = light ? 110 : 140;
    let w = 0, h = 0, raf = 0, running = true, nodes = [], sparks = [];
    const resize = () => { const r = canvas.getBoundingClientRect(); w = canvas.width = r.width * devicePixelRatio; h = canvas.height = r.height * devicePixelRatio; };
    const init = () => { nodes = Array.from({ length: N }, () => ({ x: Math.random() * w, y: Math.random() * h, vx: (Math.random() - .5) * .25 * devicePixelRatio, vy: (Math.random() - .5) * .25 * devicePixelRatio })); sparks = Array.from({ length: light ? 3 : 6 }, () => ({ a: 0, b: 1, t: Math.random() })); };
    const pick = (s) => { s.a = Math.floor(Math.random() * N); let b = Math.floor(Math.random() * N); if (b === s.a) b = (b + 1) % N; s.b = b; s.t = 0; };
    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      const L = LINK * devicePixelRatio;
      for (const n of nodes) { if (!reduce) { n.x += n.vx; n.y += n.vy; if (n.x < 0 || n.x > w) n.vx *= -1; if (n.y < 0 || n.y > h) n.vy *= -1; } }
      for (let i = 0; i < N; i++) for (let j = i + 1; j < N; j++) {
        const dx = nodes[i].x - nodes[j].x, dy = nodes[i].y - nodes[j].y, d = Math.hypot(dx, dy);
        if (d < L) { ctx.strokeStyle = `rgba(0,240,255,${(1 - d / L) * .35})`; ctx.lineWidth = devicePixelRatio; ctx.beginPath(); ctx.moveTo(nodes[i].x, nodes[i].y); ctx.lineTo(nodes[j].x, nodes[j].y); ctx.stroke(); }
      }
      for (const n of nodes) { ctx.fillStyle = "rgba(0,240,255,.9)"; ctx.beginPath(); ctx.arc(n.x, n.y, 1.6 * devicePixelRatio, 0, Math.PI * 2); ctx.fill(); }
      if (!reduce) for (const s of sparks) {
        s.t += 0.012; if (s.t >= 1) pick(s);
        const a = nodes[s.a], b = nodes[s.b], x = a.x + (b.x - a.x) * s.t, y = a.y + (b.y - a.y) * s.t;
        const g = ctx.createRadialGradient(x, y, 0, x, y, 8 * devicePixelRatio); g.addColorStop(0, "rgba(255,255,255,.95)"); g.addColorStop(1, "rgba(0,240,255,0)");
        ctx.fillStyle = g; ctx.beginPath(); ctx.arc(x, y, 8 * devicePixelRatio, 0, Math.PI * 2); ctx.fill();
      }
      if (running && !reduce) raf = requestAnimationFrame(draw);
    };
    resize(); init(); sparks.forEach(pick); draw();
    const io = new IntersectionObserver(([e]) => { running = e.isIntersecting; if (running && !reduce) { cancelAnimationFrame(raf); raf = requestAnimationFrame(draw); } }, { threshold: 0.05 });
    io.observe(canvas);
    const onR = () => { resize(); init(); sparks.forEach(pick); if (reduce) draw(); };
    window.addEventListener("resize", onR);
    return () => { running = false; cancelAnimationFrame(raf); io.disconnect(); window.removeEventListener("resize", onR); };
  }, [light, reduce]);
  return <canvas ref={ref} className="absolute inset-0 w-full h-full opacity-70" aria-hidden="true" data-testid="featured-network-canvas" />;
}

export function FeaturedCard({ data, onOpen, light, reduce }) {
  const p = data?.project;
  return (
    <motion.div initial={reduce ? false : { opacity: 0, y: 30, scale: 0.985 }} whileInView={{ opacity: 1, y: 0, scale: 1 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.9, ease }}
      data-testid="featured-project-card" className="featured-card relative rounded-3xl overflow-hidden border border-cyan-400/25 bg-[#070C1A]/90 mb-10 sm:mb-14">
      <span className="showcase-border featured-border" aria-hidden="true" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(0,240,255,.14),transparent_55%),radial-gradient(ellipse_at_bottom_right,rgba(37,99,235,.18),transparent_55%)]" aria-hidden="true" />
      <NetworkCanvas light={light} reduce={reduce} />
      <div className="relative p-7 sm:p-10 lg:p-14 grid lg:grid-cols-[1.4fr_1fr] gap-8 items-center">
        <div>
          <ComingSoonBadge size="lg" testId="featured-coming-soon" />
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white mt-5 leading-[1.05] tracking-tight" data-testid="featured-title">{data?.title}</h2>
          <p className="text-slate-300 text-base md:text-lg mt-4 max-w-xl" data-testid="featured-subtitle">{data?.subtitle}</p>
          <div className="mt-8 max-w-md">
            <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500 mb-2"><span>Development in progress</span><span className="text-cyan-300">Live roadmap</span></div>
            <div className="featured-progress h-1.5 rounded-full bg-slate-800/80 overflow-hidden" data-testid="featured-progress-line"><span className={`featured-progress-bar block h-full w-1/3 rounded-full ${reduce ? "" : "featured-progress-move"}`} /></div>
          </div>
          {p && (
            <button type="button" onClick={() => onOpen(p)} data-testid="featured-learn-more" className="mt-8 btn-glow inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold text-slate-950 hover:scale-[1.02] transition-transform">
              Explore {p.name} <ArrowRight className="w-4 h-4" />
            </button>
          )}
        </div>
        {p && (
          <div className="glass rounded-2xl p-6 relative overflow-hidden" data-testid="featured-project-detail">
            <span className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-cyan-400/15 blur-3xl showcase-glow" aria-hidden="true" />
            <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br from-cyan-500/25 to-blue-600/10 border border-cyan-400/30 flex items-center justify-center text-cyan-300 overflow-hidden showcase-icon ${reduce ? "" : "icon-float"}`}>
              {p.image_url ? <img src={p.image_url} alt="" className="w-full h-full object-cover" /> : <ProjectIcon name={p.icon} className="w-8 h-8" />}
            </div>
            <p className="label-eyebrow text-[10px] font-mono uppercase tracking-[0.22em] text-cyan-400 mt-5">{p.category} · Featured</p>
            <h3 className="font-heading text-xl font-semibold text-white mt-1">{p.name}</h3>
            <p className="text-sm text-slate-400 mt-2 leading-relaxed">{p.description}</p>
            <div className="mt-4 flex flex-wrap items-center gap-4"><StatusDot status={p.status} testId={`featured-status-${p.id}`} />{p.launch_date && <span className="text-xs text-slate-500 font-mono inline-flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5" /> {fmtDate(p.launch_date)}</span>}</div>
          </div>
        )}
      </div>
    </motion.div>
  );
}

export function ProjectDetailDialog({ project, onClose, reduce }) {
  const [label] = PROJECT_STATUS[project?.status] || PROJECT_STATUS.coming_soon;
  return (
    <Dialog open={!!project} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0A0F1D]/95 backdrop-blur-xl border-cyan-500/25 text-slate-200 max-w-2xl p-0 overflow-hidden" data-testid="project-dialog">
        {project && (
          <div className="relative">
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(0,240,255,.14),transparent_60%)] pointer-events-none" aria-hidden="true" />
            <div className="relative p-7 sm:p-9">
              <div className="flex flex-col sm:flex-row sm:items-center gap-6">
                <motion.div initial={reduce ? false : { scale: 0.6, opacity: 0, rotate: -8 }} animate={{ scale: 1, opacity: 1, rotate: 0 }} transition={{ type: "spring", stiffness: 220, damping: 18 }}
                  className="relative shrink-0">
                  <span className="absolute inset-0 rounded-3xl bg-cyan-400/25 blur-2xl showcase-glow" aria-hidden="true" />
                  <div className={`relative w-24 h-24 rounded-3xl bg-gradient-to-br from-cyan-500/25 to-blue-600/10 border border-cyan-400/40 flex items-center justify-center text-cyan-200 overflow-hidden ${reduce ? "" : "icon-float"}`} data-testid="project-dialog-icon">
                    {project.image_url ? <img src={project.image_url} alt="" className="w-full h-full object-cover" /> : <ProjectIcon name={project.icon} className="w-12 h-12" />}
                  </div>
                </motion.div>
                <div className="min-w-0">
                  <p className="label-eyebrow text-[10px] font-mono uppercase tracking-[0.22em] text-cyan-400">{project.category}</p>
                  <DialogTitle className="font-heading text-2xl sm:text-3xl font-bold text-white mt-1" data-testid="project-dialog-title">{project.name}</DialogTitle>
                  <div className="mt-3 flex flex-wrap items-center gap-3"><ComingSoonBadge label={label.toUpperCase()} testId="project-dialog-badge" /><StatusDot status={project.status} testId="project-dialog-status" /></div>
                </div>
              </div>
              <p className="text-sm sm:text-base text-slate-300 leading-relaxed mt-6" data-testid="project-dialog-description">{project.description}</p>
              <div className="grid sm:grid-cols-2 gap-6 mt-6">
                <div className="glass rounded-xl p-4 space-y-2 text-sm">
                  <div className="flex justify-between gap-3"><span className="text-slate-500">Category</span><span className="text-slate-200 text-right">{project.category}</span></div>
                  <div className="flex justify-between gap-3"><span className="text-slate-500">Status</span><span className="text-slate-200">{label}</span></div>
                  <div className="flex justify-between gap-3"><span className="text-slate-500">Expected launch</span><span className="text-slate-200 font-mono text-xs" data-testid="project-dialog-launch">{project.launch_date ? fmtDate(project.launch_date) : "To be announced"}</span></div>
                </div>
                <div>
                  <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500 mb-2">Key features</p>
                  {project.features?.length ? (
                    <motion.ul variants={seq} initial={reduce ? false : "hidden"} animate="show" className="space-y-2" data-testid="project-dialog-features">
                      {project.features.map((f, i) => <motion.li key={i} variants={item} className="flex gap-2 text-sm text-slate-300"><Check className="w-4 h-4 text-cyan-400 mt-0.5 shrink-0" />{f}</motion.li>)}
                    </motion.ul>
                  ) : <p className="text-sm text-slate-500">Details will be published closer to launch.</p>}
                </div>
              </div>
              <p className="text-[11px] text-slate-500 mt-6">Project timelines are indicative and may change. Announcements do not constitute a guarantee of launch, revenue or returns.</p>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
